/**
 * Common validation utilities and regex patterns
 * Centralized location for all validation logic across the application
 */

// ============================================================================
// REGEX PATTERNS
// ============================================================================

/**
 * Email validation regex
 * Matches: user@domain.com
 * Pattern: one or more non-whitespace/non-@ chars, @, domain, dot, TLD
 *
 * Note: This is a simple pattern. For production-grade validation,
 * consider using a library like validator.js
 */
export const EMAIL_REGEX = /^[a-z0-9._-]+@[a-z0-9.-]+\.[a-z]{2,}$/;

/**
 * OTP validation regex - exactly 6 digits
 * Used for: Final OTP validation
 */
export const OTP_REGEX = /^\d{6}$/;

/**
 * OTP input validation regex - allows 0 or more digits
 * Used for: Real-time input validation (allows partial entry)
 */
export const OTP_INPUT_REGEX = /^\d*$/;

/**
 * Non-digit characters regex (for removal)
 * Used for: Stripping non-numeric characters from input
 * Usage: value.replace(NON_DIGIT_REGEX, '')
 */
export const NON_DIGIT_REGEX = /\D/g;

/**
 * Password validation regex
 * Requirements:
 * - At least one lowercase letter [a-z]
 * - At least one uppercase letter [A-Z]
 * - At least one digit [0-9]
 * - At least one special character [$@$!%*?&]
 * - No whitespace
 * - 8-30 characters long
 */
export const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&])(?!.*\s).{8,30}$/;

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validates email address format
 * @param email - Email address to validate
 * @returns Error message if invalid, undefined if valid
 */
export const validateEmail = (email: string): string | undefined => {
  if (!email) return 'Email is required';
  if (!EMAIL_REGEX.test(email)) return 'Please enter valid email';
  return undefined;
};

/**
 * Validates OTP code
 * @param otp - OTP code to validate
 * @returns Error message if invalid, undefined if valid
 */
export const validateOTP = (otp: string): string | undefined => {
  if (!otp) return 'OTP is required';
  if (otp.length !== 6) return 'OTP must be 6 digits';
  if (!OTP_REGEX.test(otp)) return 'OTP must contain only numbers';
  return undefined;
};

/**
 * Validates password strength and length
 * @param password - Password to validate
 * @param minLength - Minimum password length (default: 8)
 * @param maxLength - Maximum password length (default: 30)
 * @returns Error message if invalid, undefined if valid
 */
export const validatePassword = (
  password: string,
  minLength: number = 8,
  maxLength: number = 30
): string | undefined => {
  if (!password) return 'Password is required.';
  if (password.length < minLength) {
    return `Password must be at least ${minLength} characters long.`;
  }
  if (password.length > maxLength) {
    return `Password must be at least maximum ${maxLength} characters long.`;
  }
  return undefined;
};

/**
 * Validates password confirmation matches original
 * @param password - Original password
 * @param confirmPassword - Confirmation password
 * @returns Error message if invalid, undefined if valid
 */
export const validatePasswordConfirmation = (
  password: string,
  confirmPassword: string
): string | undefined => {
  if (!confirmPassword) return 'Please confirm your password';
  if (password !== confirmPassword) return 'Passwords do not match';
  return undefined;
};

/**
 * Strips all non-digit characters from a string
 * @param value - String to clean
 * @returns String containing only digits
 */
export const stripNonDigits = (value: string): string => {
  return value.replace(NON_DIGIT_REGEX, '');
};

/**
 * Validates if input contains only digits
 * @param value - Value to check
 * @returns true if valid, false otherwise
 */
export const isNumericInput = (value: string): boolean => {
  return OTP_INPUT_REGEX.test(value);
};

// ============================================================================
// SECURITY VALIDATION
// ============================================================================

/**
 * Dangerous SQL keywords that could indicate SQL injection attempts
 */
const DANGEROUS_SQL_KEYWORDS = [
  'DROP',
  'DELETE',
  'TRUNCATE',
  'ALTER',
  'REVOKE',
];

/**
 * Validates SQL query for potential injection attacks
 * Only allows SELECT queries without dangerous operations
 *
 * @param query - SQL query string to validate
 * @returns Error message if invalid, undefined if valid
 */
export const validateSQLQuery = (query: string): string | undefined => {
  if (!query || query.trim().length === 0) {
    return 'Query cannot be empty';
  }

  const upperQuery = query.toUpperCase().trim();

  // Check for dangerous keywords
  for (const keyword of DANGEROUS_SQL_KEYWORDS) {
    if (upperQuery.includes(keyword)) {
      return `Query contains potentially dangerous operation: ${keyword}`;
    }
  }

  // Check for multiple statements (semicolon followed by more content)
  if (/;\s*\w/.test(query)) {
    return 'Multiple SQL statements are not allowed';
  }

  return undefined;
};
